import React, { useState, useRef, useEffect } from 'react';
import { DownOutlined } from '@ant-design/icons';
import { FramingMode, FramingRow } from '../../../types/contentFraming';
import Copilot from './Copilot';
import { useContentFramingStore } from '../../../state/contentFramingStore';

interface RadioPillProps {
  value: FramingMode;
  label: string;
  selected: boolean;
  disabled: boolean;
  onChange: (value: FramingMode) => void;
}

const RadioPill: React.FC<RadioPillProps> = ({ value, label, selected, disabled, onChange }) => {
  const baseClasses = "px-1 rounded-sm border text-xs transition-colors";
  const selectedClasses = selected ? "bg-white border-as-orange-primary text-as-orange-primary font-bold" : "bg-white text-black border-gray-300 hover:bg-gray-100";
  const disabledClasses = disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer";
  
  return (
    <button
      type="button"
      className={`${baseClasses} ${selectedClasses} ${disabledClasses}`}
      onClick={() => !disabled && onChange(value)}
      disabled={disabled}
      role="radio"
      aria-checked={selected}
    >
      {label}
    </button>
  );
};

interface ContentFramingRowProps {
  row: FramingRow;
  placeholderOptions: { value: string; label: string }[];
  notesTrue?: boolean;
  onToggle: (id: string, enabled: boolean) => void;
  onSelectionChange: (id: string, value: string) => void;
  onInputTextChange: (id: string, value: string) => void;
  onModeChange: (id: string, mode: FramingMode) => void;
  onUpdateFieldsFromShape: (id: string, shapeId: string) => void;
  onHoverShape: (shapeId: string | null) => void;
}

const ContentFramingRow: React.FC<ContentFramingRowProps> = ({
  row,
  placeholderOptions,
  notesTrue = false,
  onToggle,
  onSelectionChange,
  onInputTextChange,
  onModeChange,
  onUpdateFieldsFromShape,
  onHoverShape
}) => {
  const isDisabled = !row.enabled;
  const slideShapesData = useContentFramingStore((s) => s.slideShapesData);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [dropdownDirection, setDropdownDirection] = useState<'down' | 'up'>('down');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const rowContainerRef = useRef<HTMLDivElement>(null);
  const openCopilot = useContentFramingStore((s) => s.openCopilot);
  const closeCopilot = useContentFramingStore((s) => s.closeCopilot);
  const copilot = useContentFramingStore((s) => s.copilot);
  // Function to calculate dropdown direction based on available space
  const calculateDropdownDirection = () => {
    if (!dropdownRef.current) return 'down';
    
    const rect = dropdownRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 192; // max-h-48 = 192px
    
    // If not enough space below but enough space above, open upward
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      return 'up';
    }
    
    return 'down';
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setFocusedIndex(-1);
        onHoverShape(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onHoverShape]);

  // Reset focus when dropdown closes
  useEffect(() => {
    if (!isDropdownOpen) {
      setFocusedIndex(-1);
      onHoverShape(null);
    }
  }, [isDropdownOpen, onHoverShape]);
  
  // Close Copilot when clicking anywhere outside Copilot panel and inputs for this row
  useEffect(() => {
    if (copilot.mode === '') return () => {};
    const handleGlobalClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const inCopilot = !!target.closest(`[data-row-id="${row.id}"]`);
      const inInputs = !!target.closest('[data-copilot-input="true"]');
      if (inCopilot || inInputs) return;
      closeCopilot();
    };
    document.addEventListener('mousedown', handleGlobalClick);
    return () => document.removeEventListener('mousedown', handleGlobalClick);
  }, [copilot.mode, row.id]);

  return (
    <div className="flex w-full items-center gap-2 py-1 mb-2" ref={rowContainerRef}>
      {/* Enable Checkbox - 8% */}
      <div className="w-[2%] flex items-center" onClick={() => onHoverShape(row.selection)}>
        <input
          type="checkbox"
          id={`enable-${row.id}`}
          checked={row.enabled}
          onChange={(e) => onToggle(row.id, e.target.checked)}
          className="w-4 h-4 text-black border-as-orange-primary rounded focus:ring-as-orange-primary accent-as-orange-primary"
        />
      </div>
      
      {/* Framing Type Label - 12% */}
      <div className="w-[11%] flex items-center" onClick={() => onHoverShape(row.selection)}>
        <label className="text-sm font-medium text-gray-700">
          {row.label}
        </label>
      </div>
      
      {/* Custom Dropdown - 18% */}
      <div className="w-[20%] flex items-center relative" ref={dropdownRef}>
        <div
          onClick={() => {
            if (!isDisabled) {
              if (!isDropdownOpen) {
                setDropdownDirection(calculateDropdownDirection());
                onHoverShape(row.selection);
              }
              setIsDropdownOpen(!isDropdownOpen);
            }
          }}
          onKeyDown={(e) => {
            if (isDisabled) return;
            
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (isDropdownOpen && focusedIndex >= 0 && placeholderOptions?.[focusedIndex]) {
                // Select the focused option
                const selectedOption = placeholderOptions[focusedIndex];
                onSelectionChange(row.id, selectedOption.value);
                if (selectedOption.value && selectedOption.value !== '') {
                  onUpdateFieldsFromShape(row.id, selectedOption.value);
                }
                setIsDropdownOpen(false);
                setFocusedIndex(-1);
                onHoverShape(null);
              } else {
                // Toggle dropdown
                if (!isDropdownOpen) {
                  setDropdownDirection(calculateDropdownDirection());
                }
                setIsDropdownOpen(!isDropdownOpen);
                if (!isDropdownOpen) {
                  setFocusedIndex(-1);
                }
              }
            } else if (e.key === 'ArrowDown') {
              e.preventDefault();
              if (!isDropdownOpen) {
                setDropdownDirection(calculateDropdownDirection());
                setIsDropdownOpen(true);
                setFocusedIndex(0);
              } else {
                const nextIndex = Math.min(focusedIndex + 1, (placeholderOptions?.length || 1) - 1);
                setFocusedIndex(nextIndex);
                if (placeholderOptions?.[nextIndex]) {
                  onHoverShape(placeholderOptions[nextIndex].value);
                }
              }
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              if (!isDropdownOpen) {
                setDropdownDirection(calculateDropdownDirection());
                setIsDropdownOpen(true);
                setFocusedIndex((placeholderOptions?.length || 1) - 1);
              } else {
                const prevIndex = Math.max(focusedIndex - 1, 0);
                setFocusedIndex(prevIndex);
                if (placeholderOptions?.[prevIndex]) {
                  onHoverShape(placeholderOptions[prevIndex].value);
                }
              }
            } else if (e.key === 'Escape') {
              setIsDropdownOpen(false);
              setFocusedIndex(-1);
              onHoverShape(null);
            }
          }}
          tabIndex={isDisabled ? -1 : 0}
          role="combobox"
          aria-expanded={isDropdownOpen}
          aria-haspopup="listbox"
          className={`w-full px-1 py-2 border rounded-lg text-sm transition-colors cursor-pointer focus:outline-none flex items-center justify-between ${
            isDisabled 
              ? 'opacity-50 cursor-not-allowed bg-gray-100' 
              : 'bg-white hover:border-gray-400 focus:border-black'
          }`}
        >
          <span className="truncate pr-2">{
            (row.selection && (slideShapesData.find(s => s.id === row.selection)?.label))
            || placeholderOptions?.find(option => option.value === row.selection)?.label
            || 'Select a placeholder'
          }</span>
          <DownOutlined className="text-gray-500 text-xs" />
        </div>
        
        {isDropdownOpen && !isDisabled && (
          <div 
            className={`absolute left-0 right-0 z-50 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto ${
              dropdownDirection === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
            }`}
            role="listbox"
          >
            {placeholderOptions?.map((option, index) => (
              <div
                key={option.value}
                className={`px-3 py-2 text-sm cursor-pointer ${
                  index === focusedIndex 
                    ? 'bg-blue-100 text-blue-900' 
                    : 'hover:bg-gray-100'
                }`}
                onClick={() => {
                  onSelectionChange(row.id, option.value);
                  if (option.value && option.value !== '') {
                    onUpdateFieldsFromShape(row.id, option.value);
                  }
                  setIsDropdownOpen(false);
                  setFocusedIndex(-1);
                }}
                onMouseEnter={() => {
                  setFocusedIndex(index);
                  if (option.value && option.value !== '') {
                    onHoverShape(option.value);
                  }
                }}
                onMouseLeave={() => {
                  onHoverShape(null);
                }}
                role="option"
                aria-selected={index === focusedIndex}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Disabled Text Input/Textarea - 22.5% */}
      <div className="w-[25%] flex items-center" onClick={() => onHoverShape(row.selection)}>
        {notesTrue ? (
          <textarea
            value={row.disabledText}
            readOnly
            disabled
            rows={3}
            className="w-full px-1 py-2 border rounded-lg text-sm bg-gray-100 text-gray-500 cursor-not-allowed resize-none"
          />
        ) : (
          <input
            type="text"
            value={row.disabledText}
            readOnly
            disabled
            className="w-full px-1 py-2 border rounded-lg text-sm bg-gray-100 text-gray-500 cursor-not-allowed"
          />
        )}
      </div>
      
      {/* Editable Text Input/Textarea - 22.5% */}
      <div className="w-[25%] flex items-center">
        {notesTrue ? (
          <textarea
            value={row.inputText}
            onChange={(e) => onInputTextChange(row.id, e.target.value)}
            disabled={isDisabled}
            placeholder="Enter text..."
            rows={3}
            className={`w-full px-1 py-2 border rounded-lg text-sm transition-colors resize-none ${
              isDisabled 
                ? 'opacity-50 cursor-not-allowed bg-gray-100' 
                : 'bg-white hover:border-gray-400 focus:border-black focus:ring-1 focus:ring-black'
            }`}
            data-copilot-input="true"
            onFocus={(e) => {
              onHoverShape(row.selection);
              const rect = e.currentTarget.getBoundingClientRect();
              openCopilot(row.id, rect.left - 26, rect.bottom - 2);
            }}
            onBlur={() => {
              onHoverShape(null);
            }}
          />
        ) : (
          <input
            type="text"
            value={row.inputText}
            onChange={(e) => onInputTextChange(row.id, e.target.value)}
            disabled={isDisabled}
            placeholder="Enter text..."
            className={`w-full px-1 py-2 border rounded-lg text-sm transition-colors ${
              isDisabled 
                ? 'opacity-50 cursor-not-allowed bg-gray-100' 
                : 'bg-white hover:border-gray-400 focus:border-black focus:ring-1 focus:ring-black'
            }`}
            data-copilot-input="true"
            onFocus={(e) => {
              onHoverShape(row.selection);
              const rect = e.currentTarget.getBoundingClientRect();
              openCopilot(row.id, rect.left - 26, rect.bottom - 2);
            }}
            onBlur={() => {
              onHoverShape(null);
            }}
          />
        )}
      </div>
      
      {/* Radio Pills - 17% */}
      <div className="w-[16%] flex items-center" role="radiogroup" aria-label={`Mode selection for ${row.label}`} onClick={() => onHoverShape(row.selection)}>
        <RadioPill
          value="placeholderDesign"
          label="Placeholder Design"
          selected={row.mode === 'placeholderDesign'}
          disabled={isDisabled}
          onChange={(mode) => onModeChange(row.id, mode)}
        />
         <RadioPill
          value="formatTemplate"
          label="Format Template"
          selected={row.mode === 'formatTemplate'}
          disabled={isDisabled}
          onChange={(mode) => onModeChange(row.id, mode)}
        />
      </div>
      {/* Floating Co-Pilot icon */}
      <Copilot 
        tooltip="Alpha Agent" 
        onClose={() => closeCopilot()}
      />
    </div>
  );
};


export default ContentFramingRow;